import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TikTokIcon from "../components/TikTokIcon";
import { submitContact } from "../lib/api";
import { BRAND } from "../lib/brand";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Votre message a bien été envoyé.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#050505] min-h-screen">
      <Navbar />

      <div className="pt-40 pb-24 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-20 max-w-3xl reveal">
          <p className="label-caps text-[color:var(--gold)] mb-6">Nous contacter</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95]">
            Une <em className="italic text-[color:var(--gold-soft)]">correspondance</em><br />privilégiée.
          </h1>
          <p className="text-white/60 font-light mt-8 max-w-xl leading-relaxed">
            Notre conciergerie est à votre disposition pour vous accompagner dans le choix d'un parfum, une commande sur mesure ou toute question sur la Maison.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 lg:gap-20">
          {/* Left: contact info */}
          <aside className="lg:col-span-2 space-y-10">
            <div className="border-t border-white/10 pt-8">
              <p className="label-caps text-[color:var(--gold)] mb-4 text-[10px]">Adresse électronique</p>
              <a
                data-testid="contact-email-link"
                href={`mailto:${BRAND.email}`}
                className="flex items-center gap-4 text-white text-xl font-serif hover:text-[color:var(--gold)] transition-colors"
              >
                <Mail size={18} strokeWidth={1.2} className="text-[color:var(--gold)]" />
                {BRAND.email}
              </a>
            </div>

            <div className="border-t border-white/10 pt-8">
              <p className="label-caps text-[color:var(--gold)] mb-4 text-[10px]">Téléphone · WhatsApp</p>
              <a
                data-testid="contact-phone-link"
                href={`tel:${BRAND.phone_link}`}
                className="flex items-center gap-4 text-white text-xl font-serif hover:text-[color:var(--gold)] transition-colors"
              >
                <Phone size={18} strokeWidth={1.2} className="text-[color:var(--gold)]" />
                {BRAND.phone_display}
              </a>
              <a
                data-testid="contact-whatsapp-link"
                href={BRAND.whatsapp}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-4 inline-flex items-center gap-3 label-caps text-[color:var(--gold)] border-b border-[color:var(--gold)] pb-1"
              >
                <MessageCircle size={14} /> Écrire sur WhatsApp
              </a>
            </div>

            <div className="border-t border-white/10 pt-8">
              <p className="label-caps text-[color:var(--gold)] mb-4 text-[10px]">Réseaux sociaux</p>
              <a
                data-testid="contact-tiktok-link"
                href={BRAND.tiktok}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-4 text-white text-xl font-serif hover:text-[color:var(--gold)] transition-colors group"
              >
                <TikTokIcon size={20} className="text-[color:var(--gold)]" />
                @wattisera
                <span className="label-caps text-white/40 text-[10px] group-hover:text-[color:var(--gold)]">TikTok</span>
              </a>
            </div>

            <div className="border-t border-white/10 pt-8">
              <p className="label-caps text-[color:var(--gold)] mb-4 text-[10px]">Adresse postale</p>
              <div className="flex items-start gap-4 text-white/85 font-light leading-relaxed">
                <MapPin size={18} strokeWidth={1.2} className="text-[color:var(--gold)] mt-1 shrink-0" />
                <address className="not-italic">
                  MAISON DOUBA<br />
                  {BRAND.address_line1}<br />
                  {BRAND.address_line2}<br />
                  {BRAND.address_country}
                </address>
              </div>
            </div>

            <div className="border-t border-white/10 pt-8">
              <p className="label-caps text-[color:var(--gold)] mb-4 text-[10px]">Horaires</p>
              <p className="text-white/70 font-light">Lundi — Samedi · 10h à 19h</p>
              <p className="text-white/40 font-light text-sm mt-1">Réponse sous 24h ouvrées</p>
            </div>
          </aside>

          {/* Right: form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div data-testid="contact-sent" className="border border-white/10 p-12 text-center">
                <div className="w-14 h-14 mx-auto rounded-full border border-[color:var(--gold)] flex items-center justify-center mb-6">
                  <Check size={22} className="text-[color:var(--gold)]" />
                </div>
                <p className="label-caps text-[color:var(--gold)] mb-3">Message envoyé</p>
                <h3 className="font-serif text-3xl text-white mb-4">Merci de votre message.</h3>
                <p className="text-white/60 font-light max-w-md mx-auto">
                  Notre conciergerie vous répondra sous 24h ouvrées à l'adresse indiquée.
                </p>
                <button onClick={() => setSent(false)} data-testid="contact-send-another" className="btn-outline mt-8">Envoyer un autre message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-white/10 p-8 md:p-12 space-y-8">
                <p className="label-caps text-[color:var(--gold)] mb-2">Formulaire de contact</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <input data-testid="contact-name" required placeholder="Nom complet" className="input-line" value={form.name} onChange={change("name")} />
                  <input data-testid="contact-email" required type="email" placeholder="Email" className="input-line" value={form.email} onChange={change("email")} />
                  <input data-testid="contact-phone" placeholder="Téléphone (facultatif)" className="input-line" value={form.phone} onChange={change("phone")} />
                  <input data-testid="contact-subject" required placeholder="Sujet" className="input-line" value={form.subject} onChange={change("subject")} />
                </div>
                <textarea
                  data-testid="contact-message"
                  required
                  rows={6}
                  placeholder="Votre message"
                  className="input-line resize-none"
                  value={form.message}
                  onChange={change("message")}
                />
                <p className="text-xs text-white/40 font-light">
                  Votre message sera envoyé à <span className="text-[color:var(--gold)]">{BRAND.email}</span>. Nous ne conservons aucune donnée à des fins publicitaires.
                </p>
                <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-gold w-full">
                  {loading ? "Envoi..." : <>Envoyer le message <ArrowRight size={14} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
